/**
 * CommandPalette 資料源聚合
 * ──────────────────────────────────────────────
 * 將以下幾種資料聚合為統一的 CommandItem[]：
 *   - 頁面（從 layout-menu-config 抽取所有有 path key 的葉節點）
 *   - 客戶 / 申請 / 證書 / 任務（mock 資料，待後端 /api/search 上線後改寫）
 *   - 命令動作（直接導航帶 query 觸發 Drawer / Form）
 *
 * 設計理念：
 *   - 所有 item 都實作 `to`（路徑）或 `action`（自訂回調）；CommandPalette
 *     只需識別兩者其一即可觸發跳轉，不必為每種型別寫分支邏輯。
 *   - icon 並非由本檔案提供；CommandPalette 會根據 `type` 渲染對應 lucide 圖示，
 *     令本檔案得以保持為純 .ts（無 JSX）並便於做為純資料層被測試。
 */
import {
  getAdminMenuItems,
  getAuditorMenuItems,
  getDeciderMenuItems,
  type MenuItem,
} from '@/components/layout/layout-menu-config';

/** Command item 的型別分類 */
export type CommandItemType =
  | 'page'
  | 'client'
  | 'application'
  | 'certificate'
  | 'task'
  | 'command';

/** Command item 結構 */
export interface CommandItem {
  /** 唯一 id（用於 React key 與 LocalStorage 最近使用紀錄） */
  id: string;
  /** 分類 */
  type: CommandItemType;
  /** 主標題（顯示用） */
  title: string;
  /** 副標題（編號 / 類別 / 路徑等輔助說明） */
  subtitle?: string;
  /** 編號（mono 字體呈現） */
  code?: string;
  /** 命中關鍵字（與 fuzzy 搜尋比對） */
  keywords?: string[];
  /** 跳轉路徑（與 action 二擇一） */
  to?: string;
  /** 自訂動作（與 to 二擇一） */
  action?: () => void;
}

/* ════════════════════════════════════════════════════════
 * 頁面：從三角色菜單抽取所有 leaf node
 * ════════════════════════════════════════════════════════ */

/**
 * 將菜單項展平為 leaf {key, label, parentLabel?} 陣列
 * 葉節點 = key 以 `/` 開頭（path 形式）
 */
function flattenLeaves(
  items: MenuItem[],
  parentLabel?: string,
): Array<{ key: string; label: string; parentLabel?: string }> {
  const out: Array<{ key: string; label: string; parentLabel?: string }> = [];
  for (const item of items) {
    if (!item) continue;
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const keyStr = String((item as { key?: unknown }).key ?? '');
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const labelStr = String((item as { label?: unknown }).label ?? '');
    const children = (item as { children?: MenuItem[] }).children;

    if (children && children.length > 0) {
      out.push(...flattenLeaves(children, labelStr));
    } else if (keyStr.startsWith('/')) {
      out.push({ key: keyStr, label: labelStr, parentLabel });
    }
  }
  return out;
}

/**
 * 取得所有頁面（合併三角色菜單，並以 path 去重）
 */
export function getPages(): CommandItem[] {
  /* 合併四角色菜單，以 path 去重，確保命令面板包含所有可訪問頁面 */
  const all = [
    ...flattenLeaves(getAdminMenuItems()),
    ...flattenLeaves(getAuditorMenuItems()),
    ...flattenLeaves(getDeciderMenuItems()),
  ];
  /* 以 path 去重，保留首次出現 */
  const seen = new Set<string>();
  const pages: CommandItem[] = [];
  for (const leaf of all) {
    if (seen.has(leaf.key)) continue;
    seen.add(leaf.key);
    pages.push({
      id: `page:${leaf.key}`,
      type: 'page',
      title: leaf.label,
      subtitle: leaf.parentLabel ? `${leaf.parentLabel} · ${leaf.key}` : leaf.key,
      keywords: [leaf.label, leaf.key, leaf.parentLabel ?? ''],
      to: leaf.key,
    });
  }
  return pages;
}

/* ════════════════════════════════════════════════════════
 * Mock 資料：客戶 / 申請 / 證書 / 任務
 * ════════════════════════════════════════════════════════ */

/** 客戶 mock — 涵蓋 6 種 scheme 與不同地區 */
export function getMockClients(): CommandItem[] {
  const mocks = [
    { code: 'CLI-2026-0001', name: '清香茶業有限公司', scheme: '有機農產品' },
    { code: 'CLI-2026-0002', name: '永耕米食實業股份', scheme: 'TAP 產銷履歷' },
    { code: 'CLI-2026-0003', name: '昇平蔬菜合作社', scheme: '有機農產品' },
    { code: 'CLI-2026-0004', name: '陽光果園股份', scheme: '產銷履歷' },
    { code: 'CLI-2026-0005', name: '海風水產加工廠', scheme: '友善環境耕作' },
    { code: 'CLI-2026-0006', name: '青田有機合作社', scheme: '有機加工' },
    { code: 'CLI-2026-0007', name: '玉山牧場', scheme: '人道飼養' },
    { code: 'CLI-2026-0008', name: '日昇咖啡莊園', scheme: '有機加工' },
  ];
  return mocks.map((m) => ({
    id: `client:${m.code}`,
    type: 'client',
    title: m.name,
    subtitle: m.scheme,
    code: m.code,
    keywords: [m.name, m.code, m.scheme],
    to: `/settings/clients?q=${encodeURIComponent(m.code)}`,
  }));
}

/** 申請 mock */
export function getMockApplications(): CommandItem[] {
  const mocks = [
    {
      code: 'APP-2026-0102',
      client: '清香茶業有限公司',
      scheme: '有機農產品',
      status: '審查中',
    },
    {
      code: 'APP-2026-0098',
      client: '永耕米食實業股份',
      scheme: 'TAP 產銷履歷',
      status: '已受理',
    },
    {
      code: 'APP-2026-0091',
      client: '昇平蔬菜合作社',
      scheme: '有機農產品',
      status: '補件中',
    },
    {
      code: 'APP-2026-0085',
      client: '陽光果園股份',
      scheme: '產銷履歷',
      status: '已派案',
    },
    {
      code: 'APP-2026-0079',
      client: '海風水產加工廠',
      scheme: '友善環境耕作',
      status: '稽核排程',
    },
    {
      code: 'APP-2026-0072',
      client: '青田有機合作社',
      scheme: '有機加工',
      status: '報告審查',
    },
    {
      code: 'APP-2026-0066',
      client: '玉山牧場',
      scheme: '人道飼養',
      status: '驗證決定',
    },
  ];
  return mocks.map((m) => ({
    id: `application:${m.code}`,
    type: 'application',
    title: m.client,
    subtitle: `${m.scheme} · ${m.status}`,
    code: m.code,
    keywords: [m.code, m.client, m.scheme, m.status],
    to: `/applications/list?q=${encodeURIComponent(m.code)}`,
  }));
}

/** 證書 mock */
export function getMockCertificates(): CommandItem[] {
  const mocks = [
    { code: 'CERT-2024-0042', client: '清香茶業有限公司', status: '有效' },
    { code: 'CERT-2024-0038', client: '永耕米食實業股份', status: '有效' },
    { code: 'CERT-2023-0211', client: '昇平蔬菜合作社', status: '即將到期' },
    { code: 'CERT-2024-0017', client: '陽光果園股份', status: '有效' },
    { code: 'CERT-2024-0008', client: '海風水產加工廠', status: '暫停中' },
    { code: 'CERT-2023-0190', client: '青田有機合作社', status: '已到期' },
    { code: 'CERT-2024-0050', client: '玉山牧場', status: '已撤銷' },
  ];
  return mocks.map((m) => ({
    id: `certificate:${m.code}`,
    type: 'certificate',
    title: m.client,
    subtitle: m.status,
    code: m.code,
    keywords: [m.code, m.client, m.status],
    to: `/certificates/list?q=${encodeURIComponent(m.code)}`,
  }));
}

/** 稽核任務 mock */
export function getMockTasks(): CommandItem[] {
  const mocks = [
    { code: 'AUD-2026-0042', client: '清香茶業有限公司', status: '進行中' },
    { code: 'AUD-2026-0038', client: '永耕米食實業股份', status: '組員審核' },
    { code: 'AUD-2026-0033', client: '昇平蔬菜合作社', status: '隊長審核' },
    { code: 'AUD-2026-0029', client: '陽光果園股份', status: '草稿' },
    { code: 'AUD-2026-0024', client: '海風水產加工廠', status: 'Admin 確認中' },
    { code: 'AUD-2026-0019', client: '青田有機合作社', status: '已完成' },
    { code: 'AUD-2026-0014', client: '玉山牧場', status: '已退回' },
  ];
  return mocks.map((m) => ({
    id: `task:${m.code}`,
    type: 'task',
    title: m.client,
    subtitle: m.status,
    code: m.code,
    keywords: [m.code, m.client, m.status],
    to: `/audit-tasks/list?q=${encodeURIComponent(m.code)}`,
  }));
}

/* ════════════════════════════════════════════════════════
 * 命令：跳轉至特定頁面 + 觸發動作
 * ════════════════════════════════════════════════════════ */

/** 全域命令清單 */
export function getCommands(): CommandItem[] {
  return [
    {
      id: 'command:bulletin-create',
      type: 'command',
      title: '新增公告',
      subtitle: '前往公佈欄並開啟新增表單',
      keywords: ['公告', '公佈', 'bulletin', 'announcement', 'create'],
      to: '/bulletin?action=create',
    },
    {
      id: 'command:client-create',
      type: 'command',
      title: '新建客戶',
      subtitle: '前往客戶管理並建立新檔案',
      keywords: ['客戶', 'client', 'create', '新建'],
      to: '/settings/clients?action=create',
    },
    {
      id: 'command:case-assign',
      type: 'command',
      title: '派案',
      subtitle: '前往派案專區',
      keywords: ['派案', 'assign', 'case'],
      to: '/case-assignment',
    },
    {
      id: 'command:application-create',
      type: 'command',
      title: '新建申請',
      subtitle: '前往申請列表並開啟新增表單',
      keywords: ['申請', 'application', 'new', '新建'],
      to: '/applications/list?action=create',
    },
    {
      id: 'command:audit-task-create',
      type: 'command',
      title: '新建稽核任務',
      subtitle: '前往任務列表並開啟新增 Drawer',
      keywords: ['稽核任務', 'audit', 'task', 'create'],
      to: '/audit-tasks/list?action=create',
    },
    {
      id: 'command:report-upload',
      type: 'command',
      title: '上傳稽核報告',
      subtitle: '前往報告管理',
      keywords: ['報告', '上傳', 'report', 'upload'],
      to: '/auditor/reports?action=upload',
    },
  ];
}

/* ════════════════════════════════════════════════════════
 * 統一聚合
 * ════════════════════════════════════════════════════════ */

/**
 * 取得所有 CommandItem（命令優先排在最前，其後為頁面、客戶、申請、證書、任務）
 * 注意：命中後的最終排序由 CommandPalette 內的 fuzzy score 決定，這裡的順序
 *      只用於空查詢時的預設排序展示。
 */
export function getAllItems(): CommandItem[] {
  return [
    ...getCommands(),
    ...getPages(),
    ...getMockClients(),
    ...getMockApplications(),
    ...getMockCertificates(),
    ...getMockTasks(),
  ];
}
